import { Button, Card, Checkbox, Col, Divider, Flex, Form, Radio, Row, Skeleton, Typography } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// components
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';
import DatePickerElement from 'components/datePicker/datePicker';
import DisabledElement from 'components/disabledElement/disabledElement';
import { VerificationPayload } from '../../commonModels';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { onCalculateTotalAmount, onCheckEligibilityDate, onGetDividendById, onUpdateDividend } from '../services/dividends';
import NotificationService from 'core/services/notification';
import dayjs from 'dayjs';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import { useDebouncedCallback } from 'use-debounce';
import VerificationModal from '../../verificationModal';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import { CreateDividendPayload, Dividend } from '../models/dividends';
import AbsteensComponent from '../components/absteensComponents';
import DirectIbanComponent from '../components/directIbanComponents';

// services

// styles

// models

interface DividendEditPageProps {}

const DividendEditPage: FunctionComponent<DividendEditPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams<{ id: string }>();

	const [updateForm] = Form.useForm();

	const eligibilityDateWatcher = Form.useWatch('eligibility_date', updateForm);
	const revenuePerShareWatcher = Form.useWatch('revenue_per_share', updateForm);
	const forbiddenExceptedShareholdersWatcher: Dividend['forbidden_excepted_shareholders'] = Form.useWatch(
		'forbidden_excepted_shareholders',
		updateForm,
	);
	const directTransferExceptedShareholdersWatcher: Dividend['direct_transfer_excepted_shareholders'] = Form.useWatch(
		'direct_transfer_excepted_shareholders',
		updateForm,
	);

	const [isLoading, setIsLoading] = useState<boolean>(true);

	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	const [isCheckingEligibilityDate, setIsCheckingEligibilityDate] = useState<boolean>(false);
	const [isValidEligibilityDate, setIsValidEligibilityDate] = useState<boolean>(true);

	const [isCalculatingTotalAmount, setIsCalculatingTotalAmount] = useState<boolean>(false);
	const [totalAmount, setTotalAmount] = useState<string>('0');

	const [acknowledge1, setAcknowledge1] = useState<boolean>(false);
	const [acknowledge2, setAcknowledge2] = useState<boolean>(false);

	useEffect(() => {
		getDividendRequest();
	}, []);

	useEffect(() => {
		calculateTotalAmount();
	}, [forbiddenExceptedShareholdersWatcher]);

	const getDividendRequest = async () => {
		try {
			setIsLoading(true);

			const response = await onGetDividendById(id as string);
			updateForm.setFieldsValue({
				interval_of_distribution: response.action.interval_of_distribution_key,
				revenue_per_share: parseFloat(response.action.revenue_per_share),
				eligibility_date: dayjs(response.action.eligibility_date),
				distribution_date: dayjs(response.action.distribution_date),
				direct_transfer_excepted_shareholders: response.action.direct_transfer_excepted_shareholders,
				forbidden_excepted_shareholders: response.action.forbidden_excepted_shareholders,
			});
			setTotalAmount(response.action.total_amount_of_dividends);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/update-documents');
		}
	};

	const checkEligibilityDate = async (date: string) => {
		try {
			if (!date) return;

			setIsCheckingEligibilityDate(true);

			const response = await onCheckEligibilityDate(date);
			setIsValidEligibilityDate(response.isValidEligibilityDate);

			setIsCheckingEligibilityDate(false);
		} catch (error: any) {
			setIsValidEligibilityDate(false);
			setIsCheckingEligibilityDate(false);

			if (error?.response?.data?.data?.errors?.eligibility_date?.[0]) {
				updateForm.setFields([
					{
						name: 'eligibility_date',
						errors: [error?.response?.data?.data?.errors?.eligibility_date?.[0]],
					},
				]);

				return;
			}

			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const calculateTotalAmount = async () => {
		try {
			const date = updateForm.getFieldValue('eligibility_date');
			const revenue_per_share = updateForm.getFieldValue('revenue_per_share');

			if (!date || !revenue_per_share) return;

			setIsCalculatingTotalAmount(true);

			const response = await onCalculateTotalAmount(
				dayjs(date).format('YYYY-MM-DD'),
				revenue_per_share,
				forbiddenExceptedShareholdersWatcher?.map((item) => item.id) || [],
				directTransferExceptedShareholdersWatcher?.map((item) => item.id) || [],
			);
			setTotalAmount(response.totalAmount);

			updateForm.setFieldsValue({
				forbidden_excepted_shareholders: response.forbidden_excepted_shareholders,
				direct_transfer_excepted_shareholders: response.direct_transfer_excepted_shareholders,
			});

			setIsCalculatingTotalAmount(false);
		} catch (error: any) {
			setIsCalculatingTotalAmount(false);

			if (error?.response?.data?.data?.errors?.eligibility_date?.[0]) {
				updateForm.setFields([
					{
						name: 'eligibility_date',
						errors: [error?.response?.data?.data?.errors?.eligibility_date?.[0]],
					},
				]);

				return;
			}

			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const calculateTotalAmountDebounced = useDebouncedCallback(calculateTotalAmount, 800);

	const onSubmit = async (otp: VerificationPayload) => {
		try {
			setIsSubmitting(true);

			const payload: CreateDividendPayload = {
				otp: otp.key,
				interval_of_distribution: updateForm.getFieldValue('interval_of_distribution'),
				revenue_per_share: updateForm.getFieldValue('revenue_per_share').toString(),
				eligibility_date: dayjs(updateForm.getFieldValue('eligibility_date')).format('YYYY-MM-DD'),
				distribution_date: dayjs(updateForm.getFieldValue('distribution_date')).format('YYYY-MM-DD'),
				forbidden_excepted_shareholders: updateForm.getFieldValue('forbidden_excepted_shareholders')?.map((item: any) => item.id) || [],
				direct_transfer_excepted_shareholders: updateForm.getFieldValue('direct_transfer_excepted_shareholders')?.map((item: any) => item.id) || [],
			};

			await onUpdateDividend(id!, payload);

			navigate('/dashboard/actions/dividends');
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.DIVIDENDS_UPDATED'));
		} catch (error: any) {
			setIsSubmitting(false);
			setVerificationModalOpen(false);

			const message = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');

			NotificationService.error(t('COMMON.ERROR'), message);

			mapResponseErrors(error, updateForm);
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			<Card>
				<ComponentHeader title={t('ACTIONS.DIVIDENDS')} subtitle={t('ACTIONS.FILL_TO_BUILD')} />

				{isLoading ? (
					<LoadingComponent />
				) : (
					<Form form={updateForm} initialValues={{ interval_of_distribution: 'Interim', revenue_per_share: 0 }}>
						<h6 className="fw-medium mt-4">{t('ACTIONS.DISTRIBUTION_INTERVAL')}</h6>
						<Form.Item name="interval_of_distribution" rules={[{ required: true, message: t('COMMON.REQUIRED_FIELD') }]}>
							<Radio.Group
								className="customRadio"
								style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
								options={[
									{ label: t('ACTIONS.INTERIM_DIVIDENDS'), value: 'Interim' },
									{ label: t('ACTIONS.ANNUAL_DIVIDENDS'), value: 'Annual' },
								]}
							/>
						</Form.Item>

						<Divider />

						<Row gutter={[16, 16]}>
							<Col span={24} md={12} lg={6}>
								<DatePickerElement
									name="eligibility_date"
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
									label={t('ACTIONS.ELIGIBILITY_DATE')}
									isLoading={isCheckingEligibilityDate}
									onChange={(date) => checkEligibilityDate(date?.format('YYYY-MM-DD') || '')}
									maxDate={dayjs().subtract(1, 'day')}
								/>
							</Col>
							<Col span={24} md={12} lg={6} style={{ filter: isValidEligibilityDate ? '' : 'grayscale(1)opacity(0.5)' }}>
								<DatePickerElement
									name="distribution_date"
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{
											validator: (_, value) => {
												if (!value) return Promise.resolve();

												const minDate = dayjs().add(3, 'day').startOf('day');

												if (dayjs(value).isBefore(minDate)) return Promise.reject(new Error(t('ACTIONS.DATE_MIN_3_DAYS')));

												return Promise.resolve();
											},
										},
									]}
									label={t('ACTIONS.DISTRIBUTION_DATE')}
									disabled={!isValidEligibilityDate}
									minDate={dayjs().add(3, 'day')}
								/>
							</Col>
							<Col span={24} md={12} lg={6} style={{ filter: isValidEligibilityDate ? '' : 'grayscale(1)opacity(0.5)' }}>
								<FormNumberElement
									name="revenue_per_share"
									disabled={!isValidEligibilityDate}
									label={t('ACTIONS.REVENUE_PER_SHARE')}
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{
											validator: (_, value) => {
												if (value === null || value === undefined || value <= 0) {
													return Promise.reject(new Error(t('COMMON.FIELD_REQUIRED')));
												}
												return Promise.resolve();
											},
										},
									]}
									onChange={calculateTotalAmountDebounced}
									showPrice
									precision={2}
								/>
							</Col>
							<Col span={24} md={12} lg={6} style={{ filter: isValidEligibilityDate ? '' : 'grayscale(1)opacity(0.5)' }}>
								{isCalculatingTotalAmount ? (
									<>
										<Typography.Text style={{ fontSize: 15, marginBottom: 5, display: 'block' }}>
											{t('ACTIONS.TOTAL_AMOUNT_OF_DIVIDENDS')}
										</Typography.Text>
										<Skeleton.Button active className="w-100" style={{ height: 40, maxWidth: 'var(--inputWidth)', borderRadius: 'var(--radius)' }} />
									</>
								) : (
									<DisabledElement value={totalAmount} showPrice label={t('ACTIONS.TOTAL_AMOUNT_OF_DIVIDENDS')} />
								)}
							</Col>
						</Row>

						<AbsteensComponent
							eligibility_date={eligibilityDateWatcher}
							revenue_per_share={revenuePerShareWatcher}
							form={updateForm}
							disabled={!isValidEligibilityDate}
						/>

						<DirectIbanComponent
							eligibility_date={eligibilityDateWatcher}
							revenue_per_share={revenuePerShareWatcher}
							form={updateForm}
							disabled={!isValidEligibilityDate}
						/>

						<Divider />

						<Flex justify="space-between" align="center" gap={20} wrap>
							<div>
								<div className="mb-3">
									<Checkbox onChange={(event) => setAcknowledge1(event.target.checked)}>
										<span className="fw-semibold d-block">{t('ACTIONS.TERMS_AND_CONDITIONS')}</span>
										<span className="text-light">{t('ACTIONS.AGREE_TERMS_AND_CONDITIONS')}</span>
									</Checkbox>
								</div>
								<div>
									<Checkbox onChange={(event) => setAcknowledge2(event.target.checked)}>
										<span className="fw-semibold d-block">{t('ACTIONS.THREE_DAYS_PAYMENT')}</span>
										<span className="text-light">{t('ACTIONS.FAILURE_TO_PAY_SDAD_BILL')}</span>
									</Checkbox>
								</div>
							</div>

							<Flex justify="flex-end" align="center" gap={10}>
								<Button
									type="primary"
									disabled={!(acknowledge1 && acknowledge2)}
									onClick={async () => {
										try {
											await updateForm.validateFields();
											setVerificationModalOpen(true);
										} catch (error) {}
									}}>
									{t('COMMON.UPDATE')}
								</Button>
							</Flex>
						</Flex>
					</Form>
				)}
			</Card>

			<VerificationModal
				title={t('ACTIONS.CONFIRM_DIVIDEND_CREATION')}
				subTitle={t('ACTIONS.PLEASE_CONFIRM_YOUR_REQUEST')}
				otpOnly
				open={verificationModalOpen}
				isLoading={isSubmitting}
				className="confirmationModal"
				onCancel={() => setVerificationModalOpen(false)}
				onVerificationSuccess={onSubmit}
				module="dividends_update"
				note={t('ACTIONS.DIVIDENDS_NOTE')}
				noteType="red"
			/>
		</>
	);
};

export default DividendEditPage;
